import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { getAttribution, getVisitorId, trackEvent } from "@/lib/analytics";
import { useLeadForm, leadField } from "@/lib/leadForms";
import { useAuth } from "@/context/AuthContext";
import ConsentCheckbox, { CONSENT_TEXT, validateConsent } from "@/components/ConsentCheckbox";
import PhoneInput from "@/components/PhoneInput";

export default function InquiryModal({ open, onOpenChange, programSlug = "", programTitle = "" }) {
  const { user } = useAuth();
  const cfg = useLeadForm("inquiry", open);
  const [form, setForm] = useState({ name: user?.name || "", email: user?.email || "", phone: user?.phone || "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [consent, setConsent] = useState(true);

  const submit = async (e) => {
    e.preventDefault();
    if (!validateConsent(consent)) return;
    setLoading(true);
    try {
      const { data } = await api.post("/inquiries", {
        ...form,
        program_slug: programSlug,
        attribution: getAttribution(),
        visitor_id: getVisitorId(),
        page: window.location.pathname,
        consent_given: consent,
        consent_text: CONSENT_TEXT,
      });
      trackEvent("inquiry_submit", { program_slug: programSlug });
      setDone(true);
      toast.success(data.message || "Inquiry received");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const fName = leadField(cfg, "name", { label: "Full name", required: true });
  const fEmail = leadField(cfg, "email", { label: "Email", required: true });
  const fPhone = leadField(cfg, "phone", { label: "Phone" });
  const fMsg = leadField(cfg, "message", { label: "Message (optional)" });

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setDone(false); }}>
      <DialogContent className="sm:max-w-md" data-testid="inquiry-modal">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">{cfg.title || "Talk to a counselor"}</DialogTitle>
          <DialogDescription>{programTitle ? `About ${programTitle}` : (cfg.subtitle || "Tell us what you're looking for — we'll reach out within 24 hours.")}</DialogDescription>
        </DialogHeader>
        {done ? (
          <div className="py-8 text-center" data-testid="inquiry-success">
            <div className="w-14 h-14 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">✓</div>
            <p className="font-semibold text-slate-900">{cfg.success_title || "Inquiry received"}</p>
            <p className="text-sm text-slate-500 mt-1">{cfg.success_message || "A counselor will reach out within 24 hours."}</p>
            <Button className="mt-6 bg-rose-600 hover:bg-rose-700 rounded-xl" onClick={() => onOpenChange(false)} data-testid="inquiry-done-close">Done</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="flex flex-col gap-4 mt-2">
            {fName.visible && (
              <div><Label htmlFor="inq-name">{fName.label}{fName.required && " *"}</Label>
                <Input id="inq-name" data-testid="inquiry-name-input" required={!!fName.required} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 rounded-xl" /></div>
            )}
            {fEmail.visible && (
              <div><Label htmlFor="inq-email">{fEmail.label}{fEmail.required && " *"}</Label>
                <Input id="inq-email" type="email" data-testid="inquiry-email-input" required={!!fEmail.required} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 rounded-xl" /></div>
            )}
            {fPhone.visible && (
              <div><Label htmlFor="inq-phone">{fPhone.label}{fPhone.required && " *"}</Label>
                <PhoneInput id="inq-phone" testid="inquiry-phone-input" required={!!fPhone.required} value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} wrapperCls="mt-1.5" inputCls="w-full h-10 rounded-xl border border-slate-300 bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-[#FFC72C]" /></div>
            )}
            {fMsg.visible && (
              <div><Label htmlFor="inq-msg">{fMsg.label}{fMsg.required && " *"}</Label>
                <Textarea id="inq-msg" data-testid="inquiry-message-input" required={!!fMsg.required} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={3} className="mt-1.5 rounded-xl" /></div>
            )}
            <ConsentCheckbox checked={consent} onChange={setConsent} testid="inquiry-consent" />
            <Button type="submit" disabled={loading} className="bg-rose-600 hover:bg-rose-700 rounded-xl font-semibold" data-testid="inquiry-submit-button">
              {loading ? "Sending…" : (cfg.button_label || "Request callback")}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
